// Canvas 2D compositing for 1080x1920 story card PNG

export interface StoryCardData {
  globeBlob: Blob;
  userName: string;
  handle: string;
  airlineName: string;
  baseAirport: string;
  stats: {
    totalFlights: number;
    totalNauticalMiles: number;
    totalEstimatedHours: number;
    uniqueAirportCount: number;
    countriesVisited: number;
  };
  topAirports: { iata: string; city: string; count: number }[];
  achievements: string[]; // unlocked achievement titles
  avatarUrl?: string;
}

// Dark theme colors (hardcoded — share cards always use dark mode)
const BG = '#100E14';
const CARD_BG = '#181520';
const BORDER = '#2A2533';
const TEXT = '#EDE8F2';
const TEXT_SEC = '#D0CAE0';
const TEXT_MUTED = '#9088A0';
const ACCENT = '#C9A0DC';
const CORAL = '#E88B9C';

const W = 1080;
const H = 1920;
const PAD = 56;
const FONT = '"DM Sans", sans-serif';

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function renderStoryCard(data: StoryCardData): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  // ── Background ──
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, 'rgba(201,160,220,0.03)');
  grad.addColorStop(0.4, 'transparent');
  grad.addColorStop(1, 'rgba(201,160,220,0.02)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  let y = PAD;

  // ── Brand header ──
  const iconSize = 56;
  roundRect(ctx, PAD, y, iconSize, iconSize, 16);
  const iconGrad = ctx.createLinearGradient(PAD, y, PAD + iconSize, y + iconSize);
  iconGrad.addColorStop(0, ACCENT);
  iconGrad.addColorStop(1, '#9B82B0');
  ctx.fillStyle = iconGrad;
  ctx.fill();
  ctx.font = `800 28px ${FONT}`;
  ctx.fillStyle = BG;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('R', PAD + iconSize / 2, y + iconSize / 2 + 1);

  ctx.font = `600 26px ${FONT}`;
  ctx.fillStyle = TEXT_MUTED;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.letterSpacing = '3px';
  ctx.fillText('ROST & FOUND', PAD + iconSize + 20, y + iconSize / 2);
  ctx.letterSpacing = '0px';

  y += iconSize + 18;

  // ── User section ──
  const avatarSize = 96;
  ctx.save();
  ctx.beginPath();
  ctx.arc(PAD + avatarSize / 2, y + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
  ctx.clip();

  let avatarDrawn = false;
  if (data.avatarUrl) {
    try {
      const avatarImg = await loadImage(data.avatarUrl);
      ctx.drawImage(avatarImg, PAD, y, avatarSize, avatarSize);
      avatarDrawn = true;
    } catch { /* fallback to initials */ }
  }
  if (!avatarDrawn) {
    const avGrad = ctx.createLinearGradient(PAD, y, PAD + avatarSize, y + avatarSize);
    avGrad.addColorStop(0, ACCENT);
    avGrad.addColorStop(1, '#9B82B0');
    ctx.fillStyle = avGrad;
    ctx.fillRect(PAD, y, avatarSize, avatarSize);
    ctx.fillStyle = BG;
    ctx.font = `700 40px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const initials = data.userName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    ctx.fillText(initials, PAD + avatarSize / 2, y + avatarSize / 2 + 1);
  }
  ctx.restore();

  const textX = PAD + avatarSize + 24;
  ctx.font = `700 44px ${FONT}`;
  ctx.fillStyle = TEXT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(data.userName, textX, y + 8);

  ctx.font = `400 30px ${FONT}`;
  ctx.fillStyle = TEXT_MUTED;
  ctx.fillText(`@${data.handle} · ${data.baseAirport}`, textX, y + 58);

  // Airline badge
  ctx.font = `600 28px ${FONT}`;
  const badgeText = data.airlineName;
  const badgeW = ctx.measureText(badgeText).width + 44;
  const badgeX = W - PAD - badgeW;
  const badgeY2 = y + 18;
  const badgeH = 54;
  roundRect(ctx, badgeX, badgeY2, badgeW, badgeH, 14);
  ctx.fillStyle = 'rgba(201,160,220,0.1)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(201,160,220,0.25)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.fillStyle = ACCENT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(badgeText, badgeX + badgeW / 2, badgeY2 + badgeH / 2 + 1);

  y += avatarSize + 14;

  // ── Globe (centered, slightly smaller than full-width for quality) ──
  const globeSize = 900;
  const globeX = (W - globeSize) / 2;
  const globeImg = await createImageBitmap(data.globeBlob);
  ctx.drawImage(globeImg, globeX, y, globeSize, globeSize);

  y += globeSize + 14;

  // ── Stats row ──
  const statsRowW = W - PAD * 2;
  const statsRowH = 140;
  roundRect(ctx, PAD, y, statsRowW, statsRowH, 24);
  ctx.fillStyle = CARD_BG;
  ctx.fill();
  ctx.strokeStyle = BORDER;
  ctx.lineWidth = 1;
  ctx.stroke();

  const statItems = [
    { value: String(data.stats.totalFlights), label: 'FLIGHTS' },
    { value: formatNumber(data.stats.totalNauticalMiles), label: 'NM' },
    { value: `${data.stats.totalEstimatedHours}h`, label: 'HOURS' },
    { value: String(data.stats.uniqueAirportCount), label: 'AIRPORTS' },
    { value: String(data.stats.countriesVisited), label: 'COUNTRIES' },
  ];

  const colW = statsRowW / statItems.length;
  for (let i = 0; i < statItems.length; i++) {
    const cx = PAD + colW * i + colW / 2;

    ctx.font = `800 60px ${FONT}`;
    ctx.fillStyle = ACCENT;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(statItems[i].value, cx, y + 50);

    ctx.font = `600 24px ${FONT}`;
    ctx.fillStyle = TEXT_MUTED;
    ctx.letterSpacing = '1.5px';
    ctx.fillText(statItems[i].label, cx, y + 108);
    ctx.letterSpacing = '0px';

    if (i < statItems.length - 1) {
      const dx = PAD + colW * (i + 1);
      ctx.beginPath();
      ctx.moveTo(dx, y + 28);
      ctx.lineTo(dx, y + statsRowH - 28);
      ctx.strokeStyle = BORDER;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  y += statsRowH + 10;

  // ── Achievements (inline centered) ──
  if (data.achievements.length > 0) {
    const achievementText = data.achievements.slice(0, 4).join('  ·  ');
    ctx.font = `500 30px ${FONT}`;
    ctx.fillStyle = ACCENT;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(achievementText, W / 2, y + 16);
    y += 48;
  }

  // ── Top Airports ──
  if (data.topAirports.length > 0) {
    const apCount = Math.min(data.topAirports.length, 5);
    const cardPad = 32;
    const titleH = 48;
    const rowH = 72;
    const cardH = cardPad + titleH + apCount * rowH + 12;
    const cardX = PAD;
    const cardW = W - PAD * 2;

    roundRect(ctx, cardX, y, cardW, cardH, 24);
    ctx.fillStyle = CARD_BG;
    ctx.fill();
    ctx.strokeStyle = BORDER;
    ctx.lineWidth = 1;
    ctx.stroke();

    let cy = y + cardPad;

    ctx.font = `600 26px ${FONT}`;
    ctx.fillStyle = TEXT_MUTED;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.letterSpacing = '2px';
    ctx.fillText('TOP AIRPORTS', cardX + cardPad, cy);
    ctx.letterSpacing = '0px';
    cy += titleH;

    const maxCount = data.topAirports[0]?.count ?? 1;
    const labelW = 280;
    const countW = 70;
    const barLeft = cardX + cardPad + labelW;
    const barRight = cardX + cardW - cardPad - countW;
    const barW = barRight - barLeft;
    const barH = 40;

    for (const ap of data.topAirports.slice(0, 5)) {
      const rowMid = cy + rowH / 2;

      // IATA bold
      ctx.font = `700 38px ${FONT}`;
      ctx.fillStyle = TEXT;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(ap.iata, cardX + cardPad, rowMid);

      // City name
      ctx.font = `400 32px ${FONT}`;
      ctx.fillStyle = TEXT_SEC;
      ctx.fillText(ap.city, cardX + cardPad + 100, rowMid);

      // Bar bg
      const barY = rowMid - barH / 2;
      roundRect(ctx, barLeft, barY, barW, barH, barH / 2);
      ctx.fillStyle = 'rgba(201,160,220,0.06)';
      ctx.fill();

      // Bar fill
      const fillW = Math.max(barW * 0.08, (ap.count / maxCount) * barW);
      roundRect(ctx, barLeft, barY, fillW, barH, barH / 2);
      const barGrad = ctx.createLinearGradient(barLeft, 0, barLeft + fillW, 0);
      barGrad.addColorStop(0, ACCENT);
      barGrad.addColorStop(1, CORAL);
      ctx.fillStyle = barGrad;
      ctx.fill();

      // Count
      ctx.font = `700 34px ${FONT}`;
      ctx.fillStyle = TEXT_SEC;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(ap.count), cardX + cardW - cardPad, rowMid);

      cy += rowH;
    }

    y = y + cardH + 10;
  }

  // ── Footer CTA ──
  const footerY = Math.max(y + 16, H - PAD - 40);
  ctx.font = `400 30px ${FONT}`;
  ctx.fillStyle = TEXT_MUTED;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  ctx.fillText('Find me on', W / 2 - 10, footerY);
  ctx.font = `600 30px ${FONT}`;
  ctx.fillStyle = ACCENT;
  ctx.textAlign = 'left';
  ctx.fillText('rostandfound.app', W / 2 + 10, footerY);

  // ── Export ──
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      'image/png',
    );
  });
}
